import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
    FunkrundsteuerEmpfaengerInformationen,
} from '../../../domain/model/funkrundsteuer-empfeanger-informationen.model';
import {
    FunkrundsteuerEmpfaengerAbregelmoeglichkeit,
    FunkrundsteuerEmpfaengerAbregelmoeglichkeitItem,
} from '../../../domain/model/funkrundsteuer-empfaenger-abregelmoeglichkeit.model';
import { Energieart, EnergieartItem } from '../../../domain/model/energieart.enum';
import {
    AbregelungFRELeistungsklasseItem,
} from '../../../domain/model/abregelungsinformationen-fre-leistungsklasse.enum';
import {
    EnergietraegerEnergieartMatcherService,
} from '../../../service/utils/energietraeger-energieart-matcher.service';
import { ConfirmDialogService } from '../../../service/common/dialog/confirm-dialog.service';
import { AbregelungsinformationenFre } from '../../../domain/model/abregelungsinformationen-fre.model';
import { AbregelungsinformationenFwa } from '../../../domain/model/abregelungsinformationen-fwa.model';
import { AbstractForm } from '../abstract-form.model';
import { ControlConfig, FormGroupConfig } from '../form-config.model';
import { Energietraeger } from '../../../domain/model/energietraeger';
import { MatValidators } from 'src/app/common/material/components/mat-form-field-required.directive';
import { PredicateValidator } from '../validator/predicate.validator';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import {
    AbregelinformationenIec101104DetailComponent,
} from './abregelinformationen-iec101104-detail/abregelinformationen-iec101104-detail.component';
import {
    AbregelinformationenTase2DetailComponent,
} from './abregelinformationen-tase2-detail/abregelinformationen-tase2-detail.component';
import { Feld } from '../../../domain/model/feld.enum';
import { RegelstufeItem } from '../../../domain/model/regelstufe.enum';
import { PostalCodeValidator } from '../validator/postal-code.validator';
import { PostalCodeType } from '../../../domain/model/postal-code-type.enum';
import { takeUntil } from 'rxjs/operators';
import { distinctUntilChanged, Subject } from 'rxjs';
import { AbregelungFREAnwenderadresse } from '../../../domain/model/abregelungsinformationen-fre-anwenderadresse.enum';
import { Erzeugungsanlage } from '../../../domain/model/erzeugungsanlage';

@Component({
    selector: 'redispatch-abregelung-sub-form',
    templateUrl: './abregelung-sub-form.component.html',
    styleUrls: [
        './abregelung-sub-form.component.scss',
    ],
    standalone: false,
})
export class AbregelungSubFormComponent extends AbstractForm<FunkrundsteuerEmpfaengerInformationen> implements OnInit, OnChanges, OnDestroy {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly LYT_GAP_VERTICAL = ErzeugungsanlageConfig.VERTICAL_FX_LAYOUT_GAP;
    public readonly Feld = Feld;

    public readonly REGELSTUFEN_PATTERN = new RegExp('^(\\d|[1-9]\\d|100)(,(\\d|[1-9]\\d|100))*$');
    public readonly FRE_ID_PATTERN = new RegExp('^((123\\d{5})|(5\\d{6})|(200[78]\\d{4}))$');

    @Input({ required: true }) readonly: boolean;
    @Input({ required: true }) erzeugungsanlage?: Erzeugungsanlage;
    @Input({ required: true }) energietraeger?: Energietraeger;
    @Input({ required: true }) steuerbareAbregelungRequired: boolean;

    @ViewChild(AbregelinformationenTase2DetailComponent, { static: true }) tase2Form: AbregelinformationenTase2DetailComponent;
    @ViewChild(AbregelinformationenIec101104DetailComponent, { static: true }) iecForm: AbregelinformationenIec101104DetailComponent;

    public regelstufeItemList = RegelstufeItem.getAll();
    public funkrundsteuerEmpfaengerLeistungsreduzierungList = FunkrundsteuerEmpfaengerAbregelmoeglichkeitItem.getAll();
    public energieartList = EnergieartItem.getAll();
    public funkrundsteuerEmpfaengerLeistungsklasseList = AbregelungFRELeistungsklasseItem.getAll();

    private energietraegerMatcher = inject(EnergietraegerEnergieartMatcherService);
    private confirmDialogService = inject(ConfirmDialogService);

    private onDestroy$ = new Subject<void>();

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    get formGroupConfig(): FormGroupConfig<FunkrundsteuerEmpfaengerInformationen> {
        return {
            abregelmoeglichkeit: [
                { value: null, disabled: this.readonly },
                [
                    MatValidators.required,
                    (control) => {
                        if (this.steuerbareAbregelungRequired) {
                            const abregelmoeglichkeit = control.value as FunkrundsteuerEmpfaengerAbregelmoeglichkeit;
                            if (FunkrundsteuerEmpfaengerAbregelmoeglichkeit.KEINE === abregelmoeglichkeit ||
                                FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FIX70 === abregelmoeglichkeit) {
                                return { steuerbareAbregelungRequired: true };
                            }
                        }
                        return null;
                    },
                ],
            ],
            regelstufen: [
                { value: null, disabled: this.readonly },
                [
                    MatValidators.required,
                    Validators.pattern(this.REGELSTUFEN_PATTERN),
                ],
            ],
            abregelungsinformationenFWA: this.fb.group(this.fwaFormGroupConfig),
            abregelungsinformationenFRE: this.fb.group(this.freFormGroupConfig),
            abregelungsinformationenTase2: this.tase2Form.initForm(),
            abregelungsinformationenIEC101104: this.iecForm.initForm(),
        };
    }

    get fwaFormGroupConfig(): FormGroupConfig<AbregelungsinformationenFwa> {
        return {
            idFernwirkgeraet: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFWA, Validators.required),
                ],
            ],
        };
    }

    get freFormGroupConfig(): FormGroupConfig<AbregelungsinformationenFre> {
        return {
            idFernwirkgeraet: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFRE, Validators.required),
                    Validators.pattern(this.FRE_ID_PATTERN),
                ],
            ],
            funkrundsteuerPlz: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFRE, Validators.required),
                    PostalCodeValidator.validate(PostalCodeType.GERMAN),
                ],
            ],
            anwenderadresse: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFRE, Validators.required),
                    (control) => {
                        if (control.value) {
                            if (!Object.values(AbregelungFREAnwenderadresse).includes(control.value)) {
                                return { freAnwenderadresseInvalid: true };
                            }
                        }
                        return null;
                    },
                ],
            ],
            energieart: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFRE, Validators.required),
                ],
            ],
            leistungsklasse: [
                { value: null, disabled: true },
                [
                    PredicateValidator.conditionalValidator(() => this.isLeistungsreduzierungFRE, Validators.required),
                ],
            ],
        };
    }

    public get fFwa(): ControlConfig<AbregelungsinformationenFwa> {
        return (this.f.abregelungsinformationenFWA as UntypedFormGroup).controls as ControlConfig<AbregelungsinformationenFwa>;
    }

    public get fFre(): ControlConfig<AbregelungsinformationenFre> {
        return (this.f.abregelungsinformationenFRE as UntypedFormGroup).controls as ControlConfig<AbregelungsinformationenFre>;
    }

    public get isRegelstufenDisabled(): boolean {
        return this.regelstufeItemList.length === 1;
    }

    public get isLeistungsreduzierungFWA(): boolean {
        return this.f.abregelmoeglichkeit.value === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FWA;
    }

    public get isLeistungsreduzierungFRE(): boolean {
        return this.f.abregelmoeglichkeit.value === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FRE;
    }

    public get isLeistungsreduzierungTase2(): boolean {
        return this.f.abregelmoeglichkeit.value === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.TASE2;
    }

    // show a warning dialog, if the Energieart of the Anlagentyp Primaerenergiertraeger does not match the Energieart

    public get isLeistungsreduzierungIEC101_104(): boolean {
        return this.f.abregelmoeglichkeit.value === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.IEC101_104;
    }

    ngOnInit(): void {
        this.f.abregelmoeglichkeit.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.onDestroy$),
            )
            .subscribe(value => {
                this.regelstufeItemList = RegelstufeItem.getAllForAbregelmoeglichkeit(value);
                if (this.regelstufeItemList.length === 1) {
                    this.f.regelstufen.setValue(this.regelstufeItemList[0].key);
                } else if (!this.regelstufeItemList.find(r => r.key === this.f.regelstufen.value)) {
                    if (this.erzeugungsanlage?.funkrundsteuerEmpfaengerInformationen?.abregelmoeglichkeit === value) {
                        this.f.regelstufen.setValue(this.erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.regelstufen);
                    } else {
                        this.f.regelstufen.setValue(null);
                    }
                }
            });

        this.f.regelstufen.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.onDestroy$),
            )
            .subscribe(value => {
                this.tase2Form.updateAbregelungstypSelectionList(value);
                this.iecForm.updateAbregelungstypSelectionList(value);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.steuerbareAbregelungRequired) {
            this.f.abregelmoeglichkeit.updateValueAndValidity();
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    public sanitize(info: FunkrundsteuerEmpfaengerInformationen): FunkrundsteuerEmpfaengerInformationen {
        if (this.f.regelstufen.disabled) {
            info.regelstufen = this.f.regelstufen.value; // needed due to "readonly field"
        }
        if (info.abregelmoeglichkeit !== FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FRE) {
            info.abregelungsinformationenFRE = null;
        }
        if (info.abregelmoeglichkeit !== FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FWA) {
            info.abregelungsinformationenFWA = null;
        }
        if (info.abregelmoeglichkeit !== FunkrundsteuerEmpfaengerAbregelmoeglichkeit.TASE2) {
            info.abregelungsinformationenTase2 = null;
        } else {
            info.abregelungsinformationenTase2 = this.tase2Form.sanitize(info.abregelungsinformationenTase2);
        }
        if (info.abregelmoeglichkeit !== FunkrundsteuerEmpfaengerAbregelmoeglichkeit.IEC101_104) {
            info.abregelungsinformationenIEC101104 = null;
        } else {
            info.abregelungsinformationenIEC101104 = this.iecForm.sanitize(info.abregelungsinformationenIEC101104);
        }
        return info;
    }

    public isAbregelungstypEnabled(abregelmoeglichkeit: FunkrundsteuerEmpfaengerAbregelmoeglichkeit): boolean {
        return abregelmoeglichkeit !== FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FIX70 ? true : this.energietraeger === Energietraeger.SOLAR;
    }

    // of the FunkrundsteuerEmpfaenger (FRE)
    public checkEnergieartMatchesPrimaerenergietraeger(value: Energieart) {
        if (this.isLeistungsreduzierungFRE && this.energietraeger !== null &&
            !this.energietraegerMatcher.doesEnergietraegerMatchEnergieart(this.energietraeger, value)) {

            this.confirmDialogService.confirm(
                'Die ausgewählte Energieart beim Funkrundsteuerempfänger (FRE) passt leider nicht<br/>' +
                'zu der Energieart des Primärenergieträgers des Anlagentyps.' +
                '<br/><br/>Möchten Sie die Energieart des Anlagentyps im Funkrundsteuerempfänger übernehmen?',
                'Energiearten stimmen nicht überein',
                'warn',
                'Ja, übernehmen',
                'Nein, ignorieren',
            ).then(result => {
                if (result) {
                    this.fFre.energieart.setValue(this.energietraegerMatcher.getMatchingEnergieart(this.energietraeger));
                }
            });

        }
    }
}
